import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";

import { usePlanFeature } from "hooks";

import { MailToMessage } from "scenes/Authentication/components/Layout/components";

import {
  DefaultSettingsHint,
  Modal,
  Options,
  ServiceTeamModal,
  useManageServiceTeam,
} from "./components";

function EditClientAssignmentModal({
  clientName,
  serviceTeam,
  serviceRoles,
  defaultTeamMembers,
  onAssign,
  onClose,
  isAssigning,
}) {
  const team = useManageServiceTeam(
    useMemo(() => {
      return {
        serviceTeam: serviceTeam.isDefaultSettings
          ? {
              ...serviceTeam,
              id: null,
              serviceTeamMembers: [],
              serviceTeamRoleIds: [],
            }
          : serviceTeam,
        serviceRoles: serviceRoles,
      };
    }, [serviceTeam, serviceRoles])
  );

  const {
    loading,
    error,
    eligibility: { eligible, cause } = {},
  } = usePlanFeature("service-teams");

  const shouldRenderEligibilityMessage = useCallback(() => {
    return (
      !team.loading && !team.disableActionButton() && !team.hasMatchedTeam()
    );
  }, [team]);

  const warningMessage = useMemo(() => {
    if (error) return <MailToMessage>{error}</MailToMessage>;
    return (
      !loading &&
      !eligible && (
        <MailToMessage
          keyword="upgrading"
          subject="Plan Upgrade - Service Team"
        >
          The above team selection does not exist in your hub, and it would
          result in a creation of a new service team. {cause}
        </MailToMessage>
      )
    );
  }, [loading, error, eligible, cause]);

  const handleOnSave = () => {
    const action = team.hasMatchedTeam()
      ? () => {
          return onAssign(team.matchedServiceTeamId);
        }
      : async () => {
          const serviceTeamId = await team.add();
          return serviceTeamId ? onAssign(serviceTeamId) : Promise.resolve();
        };

    return action().then(onClose);
  };

  return (
    <ServiceTeamModal
      header={
        <Modal.Header subtitle={clientName} onClose={onClose}>
          Edit Client Assignments
        </Modal.Header>
      }
      loading={team.loading || isAssigning}
      name={<Options.Layout hint={<DefaultSettingsHint />} />}
      editMode
      roles={team.roles}
      defaultTeamMembers={defaultTeamMembers}
      actionText="Save"
      disableActionButton={team.disableActionButton() || error || !eligible}
      onChangeAdvisor={team.updateSelection}
      onSave={handleOnSave}
      onClose={onClose}
      warningMessage={shouldRenderEligibilityMessage() && warningMessage}
    />
  );
}

EditClientAssignmentModal.propTypes = {
  clientName: PropTypes.string.isRequired,
  serviceTeam: PropTypes.object.isRequired,
  serviceRoles: PropTypes.array.isRequired,
  defaultTeamMembers: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  isAssigning: PropTypes.bool,
};

EditClientAssignmentModal.defaultProps = {
  isAssigning: false,
};

export default EditClientAssignmentModal;
